/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatISODateOnly } from "../../utils/utils";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import InstallationKpiCards from "./InstallationKpiCards";
import InstallationPeriodCards from "./InstallationPeriodCards";
import InstallationTrendChart from "./InstallationTrendChart";
import InstallationEventsModal from "./InstallationEventsModal";
import "../active-users/active-users.css";
import "./InstallationStatistics.css";

// Per-day CSV export (client-side, from the already-loaded rows — the table is not
// paginated, so these ARE the complete rows for the selected range; FR-018-019/025).
// Same columns as the table, in the same order and under the same headings, so a
// downloaded file and the screen can be read against each other line for line. Every key is
// a real field on the row — the shared CSV writer does a plain key lookup.
//
// The two BigQuery columns are how many INSTALLS sent pauser_app_install that day (each install
// once, not every event) per platform, stored by
// that row's Sync button (feature 108) — once; a synced day (bigquery_synced) never offers Sync
// again. A day not synced yet shows a dash. Today (UTC) cannot be synced until it is over, so the
// BigQuery columns always stop at yesterday.
// The other events each Sync stores, beside pauser_app_install (which the two BigQuery columns
// show). The table keeps them out so it fits without scrolling sideways — they are in the View
// popup (InstallationEventsModal) — but the CSV carries them, a pair of columns each.
const OTHER_EVENTS = ['first_open', 'custom_login_start', 'custom_sign_up_success', 'custom_login_success'];

const EXPORT_COLUMNS = [
  { key: 'time_stamp', label: 'Date' },
  { key: 'android_count', label: 'Android (Server)' },
  { key: 'ios_count', label: 'iOS (Server)' },
  { key: 'android_bigquery_count', label: 'Android (BigQuery)' },
  { key: 'ios_bigquery_count', label: 'iOS (BigQuery)' },
  ...OTHER_EVENTS.flatMap((e) => [
    { key: `${e}_android`, label: `Android (${e})` },
    { key: `${e}_ios`, label: `iOS (${e})` },
  ]),
];

const BQ_TITLE = "Installs that sent pauser_app_install that day, per BigQuery (GA4) — each install counted once — as stored by that day's Sync";
const PROVISIONAL_TITLE = 'Synced from BigQuery\u2019s live (intraday) table before Google finished this day, so the figures can still grow. Re-sync once the day is final.';
const AFTER_TODAY_TITLE = 'Today (UTC) can be synced once it is over and BigQuery has finished it.';

// Today as a UTC day — BigQuery days are UTC; today and later cannot be synced.
const utcToday = () => new Date().toISOString().slice(0, 10);
const SYNC_TITLE = 'Count first_open, pauser_app_install, custom_login_start, custom_sign_up_success and custom_login_success in BigQuery for this day and store the counts. A day is synced once.';

// The installs-per-day trend chart is hidden for now at the operator's request — the
// same per-day figures are in the table below. Flip this to true to bring it back;
// nothing else has to change.
const SHOW_TREND_CHART = false;

const VendingCounts = () => {
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');            // '' | 'auth' | 'load'

  const [summary, setSummary] = useState({ all_time: {}, period: null });
  const [rows, setRows] = useState([]);              // per-day rows, most-recent first

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);

  const [versions, setVersions] = useState([]);       // distinct app_version options
  const [appVersion, setAppVersion] = useState('');   // '' = ALL
  const [sortDir, setSortDir] = useState('desc');     // Date column sort direction

  const [syncingDate, setSyncingDate] = useState(null); // the day whose Sync is running
  const [eventsRow, setEventsRow] = useState(null);     // the day whose events popup is open
  const [bqStatus, setBqStatus] = useState(null);     // null | { kind: 'ok'|'error', text }

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Default to the last 30 days (matches /user-pause-count) so the comparison,
    // trend, and table all show a concrete window on first load — not all-time.
    const { fromStr, toStr } = presetRange('last30', new Date());
    setFromDate(fromStr); setToDate(toStr);
    setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
    setPreset('last30');
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) { loadVersions(); loadAll(fromDate, toDate, appVersion); }
  }, [token]);

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  // Populate the App Version dropdown once — failures leave it at "All versions".
  const loadVersions = async () => {
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/installation-app-versions`, auth());
      setVersions(res.data?.data || []);
    } catch (err) { /* dropdown stays at ALL */ }
  };

  const buildQuery = (f, t, av) => {
    const parts = [];
    if (f && t) parts.push(`from_date=${f}`, `to_date=${t}`);
    if (av) parts.push(`app_version=${encodeURIComponent(av)}`);
    return parts.length ? `?${parts.join('&')}` : '';
  };

  const loadAll = async (f, t, av = appVersion) => {
    setLoading(true); setError('');
    const range = buildQuery(f, t, av);
    try {
      const [sum, count] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/installation-summary${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/count-vending-data${range}`, auth()),
      ]);
      const s = sum.data?.data || { all_time: {} };
      setSummary({ all_time: s.all_time || {}, period: s.period || null });
      setRows(count.data?.data || []);
    } catch (err) {
      if (err?.response?.status === 401) setError('auth');
      else setError('load');
    } finally {
      setLoading(false);
    }
  };

  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    const f = fromStr || fs; const t = toStr || ts;
    setPreset(key); setFromDate(f); setToDate(t);
    setDisplayFromDate(new Date(f)); setDisplayToDate(new Date(t));
    loadAll(f, t, appVersion);
  };
  const handleSearch = () => { loadAll(fromDate, toDate, appVersion); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null); setPreset(null);
    setAppVersion('');                 // Clear also resets the App Version filter to "All".
    loadAll('', '', '');
  };
  const handleVersionChange = (e) => {
    const v = e.target.value;
    setAppVersion(v);
    loadAll(fromDate, toDate, v);
  };
  // A row's Sync button: count that one finished day in BigQuery and store it, then reload so
  // the row shows its figures and "Synced" in place of the button.
  const syncDay = async (date) => {
    setSyncingDate(date); setBqStatus(null);
    try {
      const res = await axiosJWT.post(`${baseUrl}/admin/api/installation-bigquery-sync`, { date }, auth());
      const d = res.data?.data || {};
      const provisional = d.source === 'intraday'
        ? ' — provisional: BigQuery has not finished this day yet. Re-sync it later for the final count.'
        : '.';
      setBqStatus({ kind: 'ok', text: `${date} synced: Android ${num(d.android_count)} · iOS ${num(d.ios_count)}${provisional}` });
      await loadAll(fromDate, toDate, appVersion);
    } catch (err) {
      if (err?.response?.status === 401) { setError('auth'); return; }
      setBqStatus({ kind: 'error', text: err?.response?.data?.message || `Could not sync ${date} from BigQuery. Please try again.` });
    } finally {
      setSyncingDate(null);
    }
  };

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const hasRange = !!(fromDate || toDate);
  // Per-day column sums for the table's totals footer (the top KPI cards stay
  // all-time / range-independent — see InstallationKpiCards). Each source keeps its own
  // subtotals; server and BigQuery counts are never added together.
  const viewTotals = rows.reduce((acc, r) => {
    acc.android_installs += Number(r.android_count) || 0;
    acc.ios_installs += Number(r.ios_count) || 0;
    acc.android_bigquery += Number(r.android_bigquery_count) || 0;
    acc.ios_bigquery += Number(r.ios_bigquery_count) || 0;
    return acc;
  }, { android_installs: 0, ios_installs: 0, android_bigquery: 0, ios_bigquery: 0 });
  const num = (v) => (Number(v) || 0).toLocaleString();
  // A day not synced yet has no BigQuery figure — a dash, so it is not mistaken for zero installs.
  const bqNum = (r, v) => (r.bigquery_synced ? num(v) : '—');
  // The chart always reads chronological order, independent of the table sort.
  const trendRows = [...rows].sort((a, b) => (a.time_stamp || '').localeCompare(b.time_stamp || ''));
  // Table rows honour the Date column sort (default most-recent first).
  const sortedRows = [...rows].sort((a, b) => {
    const cmp = (a.time_stamp || '').localeCompare(b.time_stamp || '');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Installations › <b>Statistics</b></span>
            <span className="au-route">/installation-data-statics</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Installation Statistics</h1>

          {/* All-time KPIs are range-independent — always shown, even in error/empty. */}
          <InstallationKpiCards allTime={summary.all_time} />

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the installation data. Please try again." onRetry={() => loadAll(fromDate, toDate)} />
          ) : (
            <>
              {/* Filters */}
              <div className="inst-filter-row">
                <DateRangeBar
                  preset={preset}
                  displayFrom={displayFromDate}
                  displayTo={displayToDate}
                  hasRange={hasRange || !!appVersion}
                  onPreset={handlePreset}
                  onFromChange={handleFromChange}
                  onToChange={handleToChange}
                  onSearch={handleSearch}
                  onClear={clearFilter}
                >
                  <label className="inst-version-filter">
                    <span className="inst-version-filter__label">App Version</span>
                    <select
                      className="inst-version-select"
                      data-testid="inst-version-select"
                      value={appVersion}
                      onChange={handleVersionChange}
                    >
                      <option value="">All</option>
                      {versions.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </label>
                </DateRangeBar>
              </div>
              {/* Selected-period comparison */}
              <InstallationPeriodCards period={summary.period} />

              {/* Trend + table (built from one per-day dataset) */}
              {SHOW_TREND_CHART && <InstallationTrendChart data={trendRows} />}

              {bqStatus && (
                <div className="inst-bq-bar">
                  <span
                    className={`inst-bq-status inst-bq-status--${bqStatus.kind}`}
                    data-testid="inst-bq-status"
                    role={bqStatus.kind === 'error' ? 'alert' : 'status'}
                  >
                    {bqStatus.text}
                  </span>
                </div>
              )}

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No installs in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No installs were recorded in the selected range.' : 'There are no installs to show yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Installs per day</h4>
                    <span className="au-spacer" />
                    <ExportButton
                      columns={EXPORT_COLUMNS}
                      rows={rows}
                      filename={`installations_${fromDate || 'all'}_${toDate || 'all'}.csv`}
                    />
                  </div>
                  <div className="au-table-wrap inst-table-wrap">
                    <table className="au-table" data-testid="inst-table">
                      <thead>
                        <tr>
                          <th
                            className="inst-sort-th"
                            data-testid="inst-sort-date"
                            onClick={toggleSort}
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(); } }}
                            aria-sort={sortDir === 'asc' ? 'ascending' : 'descending'}
                          >
                            Date <span className="inst-sort-arrow" aria-hidden="true">{sortDir === 'asc' ? '▲' : '▼'}</span>
                          </th>
                          <th>Android (Server)</th>
                          <th>iOS (Server)</th>
                          <th title={BQ_TITLE}>Android (BigQuery)</th>
                          <th title={BQ_TITLE}>iOS (BigQuery)</th>
                          <th className="inst-sync-th">Events</th>
                          <th className="inst-sync-th">BigQuery sync</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((r, index) => (
                          <tr key={r.time_stamp || index}>
                            <td data-label="Date">
                              {formatISODateOnly(r.time_stamp)}
                            </td>
                            <td data-label="Android (Server)" className="inst-num">{num(r.android_count)}</td>
                            <td data-label="iOS (Server)" className="inst-num">{num(r.ios_count)}</td>
                            <td data-label="Android (BigQuery)" className="inst-num inst-cloud">{bqNum(r, r.android_bigquery_count)}</td>
                            <td data-label="iOS (BigQuery)" className="inst-num inst-cloud">{bqNum(r, r.ios_bigquery_count)}</td>
                            <td data-label="Events" className="inst-sync">
                              {r.bigquery_synced ? (
                                <button
                                  type="button"
                                  className="au-btn au-btn--ghost inst-sync-btn"
                                  onClick={() => setEventsRow(r)}
                                  aria-label={`View events for ${r.time_stamp}`}
                                  title="Every event this day's Sync stored, Android and iOS"
                                >
                                  View
                                </button>
                              ) : (
                                <span className="inst-sync-state">—</span>
                              )}
                            </td>
                            <td data-label="BigQuery sync" className="inst-sync">
                              {r.bigquery_synced && !r.bigquery_provisional ? (
                                <span className="inst-sync-state">Synced</span>
                              ) : r.time_stamp >= utcToday() ? (
                                <span className="inst-sync-state" title={AFTER_TODAY_TITLE}>After today</span>
                              ) : (
                                <button
                                  type="button"
                                  className="au-btn au-btn--ghost inst-sync-btn"
                                  onClick={() => syncDay(r.time_stamp)}
                                  disabled={syncingDate === r.time_stamp}
                                  title={SYNC_TITLE}
                                >
                                  {syncingDate === r.time_stamp ? 'Syncing…' : r.bigquery_provisional ? 'Re-sync' : 'Sync'}
                                </button>
                              )}
                              {r.bigquery_provisional && (
                                <span className="inst-sync-state inst-provisional" title={PROVISIONAL_TITLE}>Provisional</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="inst-total-row" data-testid="inst-total-row">
                          <td data-label="Date">Total</td>
                          <td className="inst-num">{num(viewTotals.android_installs)}</td>
                          <td className="inst-num">{num(viewTotals.ios_installs)}</td>
                          <td className="inst-num inst-cloud">{num(viewTotals.android_bigquery)}</td>
                          <td className="inst-num inst-cloud">{num(viewTotals.ios_bigquery)}</td>
                          <td />
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {eventsRow && <InstallationEventsModal row={eventsRow} onClose={() => setEventsRow(null)} />}
    </div>
  );
};

export default VendingCounts;
